import React from 'react';
import { Table, Typography, Button, Select } from 'antd';
import { Colors } from '@common/tokens';
import {
  CurrencyInput,
  Icon,
  InfoTooltip,
  LoadingState,
  TimeWithTimeZone,
} from '@common/components';
import {
  MatchBreakdownType,
  MATCH_BREAKDOWN,
  MatchBreakdownQueryResponse,
} from '@scheduling/graphql';
import { useQuery } from '@common/hooks';
import { IncentiveRateDisplay } from '@scheduling/components';
import {
  formatTierOptionLabel,
  formatOverrideRateOptionLabel,
  formatBaseRateOptionLabel,
  formatCurrentRateLabel,
} from '@scheduling/services';

const { Text } = Typography;

export function MatchBreakdownDrawerTable({
  unitId,
  shiftId,
  matchBreakdownEditMode,
  setMatchBreakdownEditMode,
  wfgOverrideRates,
  setWfgOverrideRates,
  shiftEditMode,
  canEditRates,
}: {
  unitId: string;
  shiftId: string;
  matchBreakdownEditMode: boolean;
  setMatchBreakdownEditMode: (value: boolean) => void;
  wfgOverrideRates: {
    workforceGroupId: string;
    rate: number;
    moneyAmountCents: number | null;
  }[];
  setWfgOverrideRates: (
    value: {
      workforceGroupId: string;
      rate: number;
      moneyAmountCents: number | null;
    }[]
  ) => void;
  shiftEditMode: boolean;
  canEditRates: boolean;
}) {
  const { data, loading } = useQuery<MatchBreakdownQueryResponse>(
    MATCH_BREAKDOWN,
    {
      variables: { organizationUnitId: unitId, shiftGapId: shiftId },
    }
  );

  const shiftHasMatches = (data?.workforceGroupMatchBreakdown?.length || 0) > 0;
  const anyRowHasOverrideOptions = (
    data?.workforceGroupMatchBreakdown || []
  ).some((row) => row.overrideRateOptions.length > 0);
  const anyRowCanEditRate = (data?.workforceGroupMatchBreakdown || []).some(
    (row) => row.canEditRate
  );
  const usesCompactRateColumn = anyRowHasOverrideOptions;

  if (loading) {
    return <LoadingState />;
  }

  const columns = [
    {
      title: 'Group',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return record.workforceGroupName;
      },
    },
    {
      title: 'Available Staff',
      dataIndex: 'rate',
      key: 'rate',
      width: usesCompactRateColumn ? '15%' : '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return record.availableResources;
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Rate
          {canEditRates &&
            !shiftEditMode &&
            shiftHasMatches &&
            anyRowCanEditRate && (
              <Button
                type="link"
                onClick={() =>
                  setMatchBreakdownEditMode(!matchBreakdownEditMode)
                }
                style={{
                  height: 22,
                  paddingTop: 0,
                  paddingBottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Icon icon="pen" kind="solid" size="sm" />
              </Button>
            )}
        </div>
      ),
      dataIndex: 'avg_rate',
      key: 'avg_rate',
      width: usesCompactRateColumn ? '35%' : '25%',
      render: (id: string, record: MatchBreakdownType) => {
        // All override rates are stored in cents-integer end-to-end (matches
        // the upsert_workforce_group_shift_gap_overrides `rate: Int` contract).
        // moneyAmountCents disambiguates money-mode PCE options that share an
        // incentiveCents but encode different lump sums; null for static-tier,
        // free-form, and base-rate selections.
        const handleRateChange = (
          rateCents: number,
          moneyAmountCents: number | null = null
        ) => {
          const edition = {
            workforceGroupId: record.id,
            rate: rateCents,
            moneyAmountCents,
          };
          const editedWfg = wfgOverrideRates.find(
            (wfg) => wfg.workforceGroupId === record.id
          );
          if (!editedWfg) {
            setWfgOverrideRates(wfgOverrideRates.concat([edition]));
          } else {
            setWfgOverrideRates(
              wfgOverrideRates.map((wfg) =>
                record.id === wfg.workforceGroupId ? edition : wfg
              )
            );
          }
        };

        const renderReadOnlyRate = () => (
          <IncentiveRateDisplay
            displayAsLumpSum={record.displayAsLumpSum}
            formattedLumpSumRate={record.formattedLumpSumRate}
            formattedPerHourRate={(record.averageRate / 100).toLocaleString(
              'en-US',
              { style: 'currency', currency: 'USD' }
            )}
            tierLabel={record.incentiveTierLabel}
          />
        );

        if (!matchBreakdownEditMode || !record.canEditRate) {
          return renderReadOnlyRate();
        }

        // The "Base rate" option reverts the override back to the underlying
        // works_shift_match.rate (AVG of match.rate, 0 when matches don't
        // encode a rate). It must NOT fall back to averageRate, which equals
        // the persisted override when one exists — that would make the revert
        // option display (and re-apply) the prior override instead of clearing
        // it.
        const revertRateCents = record.baseRate;

        // Backend-driven options (money-PCE or duration-PCE); single Select.
        // Base rate is prepended as a "revert override" option; the remaining
        // options are sorted ascending by rate so managers can scan cheapest
        // → most expensive.
        if (record.overrideRateOptions.length > 0) {
          const sortedOverrideOptions = [...record.overrideRateOptions].sort(
            (a, b) => a.incentiveCents - b.incentiveCents
          );
          // Encode the option identity tuple (incentiveCents, moneyAmountCents)
          // as the Select value so money-mode options that share incentiveCents
          // but differ on moneyAmountCents stay distinct. The base-rate revert
          // is encoded with a null money amount.
          const encodeOption = (
            incentiveCents: number,
            moneyAmountCents: number | null
          ) => `${incentiveCents}:${moneyAmountCents ?? ''}`;
          const decodeOption = (value: string) => {
            const [incentiveStr, moneyStr] = value.split(':');
            return {
              incentiveCents: Number(incentiveStr),
              moneyAmountCents: moneyStr === '' ? null : Number(moneyStr),
            };
          };
          return (
            <Select
              labelInValue
              defaultValue={{
                value: encodeOption(record.averageRate, null),
                label: formatCurrentRateLabel(record),
              }}
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              onChange={(option) => {
                const { incentiveCents, moneyAmountCents } = decodeOption(
                  String(option.value)
                );
                handleRateChange(incentiveCents, moneyAmountCents);
              }}
              placeholder="Select incentive"
            >
              <Select.Option
                key="base-rate"
                value={encodeOption(revertRateCents, null)}
              >
                {formatBaseRateOptionLabel(
                  revertRateCents,
                  record.displayAsLumpSum,
                  record.shiftDuration
                )}
              </Select.Option>
              {sortedOverrideOptions.map((option, index) => (
                <Select.Option
                  key={`${option.displayName}-${option.incentiveCents}-${option.moneyAmountCents}-${index}`}
                  value={encodeOption(
                    option.incentiveCents,
                    option.moneyAmountCents
                  )}
                >
                  {formatOverrideRateOptionLabel(
                    option,
                    record.displayAsLumpSum,
                    record.shiftDuration
                  )}
                </Select.Option>
              ))}
            </Select>
          );
        }

        // Non-PCE fallback: static tiers then free-form. Base rate is also
        // available as an option so the override can be reverted. Tiers are
        // sorted ascending by payRate for scannability.
        if (record.staticPricingTiers.length > 0) {
          const sortedTiers = [...record.staticPricingTiers].sort(
            (a, b) => a.payRate - b.payRate
          );
          return (
            <Select
              labelInValue
              defaultValue={{
                value: record.averageRate,
                label: formatCurrentRateLabel(record),
              }}
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              onChange={(option) =>
                handleRateChange(Number(option.value), null)
              }
            >
              <Select.Option key="base-rate" value={revertRateCents}>
                {formatBaseRateOptionLabel(
                  revertRateCents,
                  record.displayAsLumpSum,
                  record.shiftDuration
                )}
              </Select.Option>
              {sortedTiers.map((tier) => (
                <Select.Option
                  key={tier.id}
                  value={Math.round(tier.payRate * 100)}
                >
                  {formatTierOptionLabel(
                    tier,
                    record.displayAsLumpSum,
                    record.shiftDuration
                  )}
                </Select.Option>
              ))}
            </Select>
          );
        }

        return (
          <CurrencyInput
            autoFocus={true}
            style={{ width: 90, textAlign: 'center' }}
            placeholder={(record.averageRate / 100).toString()}
            defaultValue={record.averageRate / 100}
            onChange={(value) => {
              if (typeof value !== 'number') return;
              handleRateChange(Math.round(value * 100));
            }}
          />
        );
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Visible in App
          <InfoTooltip
            size="sm"
            leftOffset="md"
            iconColor="elm"
            title="The date the shift will be shown and available to claim for this group in the mobile app."
          />
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return (
          <Text>
            <TimeWithTimeZone
              date={record.visibleDate}
              variant="short_format"
            />
          </Text>
        );
      },
    },
  ];

  return (
    <>
      <Table
        className="match-breakdown-table"
        columns={columns}
        dataSource={data?.workforceGroupMatchBreakdown}
        pagination={false}
        tableLayout="fixed"
      />

      <style jsx>{`
        :global(.match-breakdown-section) {
          cursor: pointer;
        }
        :global(.match-breakdown-table) {
          margin-bottom: 16px;
          margin-top: 24px;
        }
        :global(.match-breakdown-table .ant-table-row) {
          cursor: default;
        }
        :global(.match-breakdown-table .ant-table-tbody > tr > td) {
          border: none;
          padding: 16px 4px 16px 8px;
        }
        :global(.match-breakdown-table .ant-table-thead > tr > th) {
          border: none;
          background-color: ${Colors.steelLightest};
          padding-top: 10px;
          padding-bottom: 10px;
          font-weight: 400;
        }
        :global(
          .match-breakdown-table .ant-table-tbody > tr.ant-table-row:hover > td
        ) {
          background: none !important;
        }
      `}</style>
    </>
  );
}
